import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook'
import { isTabSwitchNavigation, useTabNavigation } from 'hooks/useTabNavigation'
import icons from '../Icons'
import {
	Accordion,
	Popup,
	Icon,
	Grid,
	Menu,
	List,
	Modal,
	Segment,
	Header,
	Divider,
	Label,
	Transition,
} from 'semantic-ui-react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import { actionClick } from 'store/actions/app'
import './Sidebar.scss'
import styles from './../../styles/global.module.scss'
import SidebarSearch from 'components/CustomSearch/SidebarSearch'
import { filterModulesForDisplay, filterModulesForSearch } from 'utils/moduleFilters'
import { normalizeSidebarSearch, matchesSidebarSearch, splitSidebarSearchMatches } from 'utils/sidebarSearch'
import { OPTIONAL_IMPORTATION_PROCESSES, REQUIRED_IMPORTATION_PROCESSES } from '../../importador/importationProcessRegistry'

function getWindowDimensions() {
	const { innerWidth: width, innerHeight: height } = window
	return {
		width,
		height,
	}
}

const SidebarMenu = (props) => {
	const { t } = useTranslation()
	const {
		expand,
		setExpand,
		previousExpand,
		setPreviousExpand,
		user,
		rest,
		history,
		location,
		openModal,
		setOpenModal,
		mobileOnly,
		setMobileOnly,
		lang,
		onMenuOpen,
		onTabOpen,
	} = props

// underlineMenu viene del hook de contexto, lateralMenu ahora viene del hook con persistencia
	const { underlineMenu } = useLateralMenu();
	const { lateralMenu } = useUniversalLateralMenuHook('user');
	const { tabs: openTabs, activeTabId } = useTabNavigation();
	// refs para evitar re-auto-abrir cuando el usuario cerró manualmente en la misma ruta
	const manualToggleForPath = useRef(null)
	const [openHeaderIndices, setOpenHeaderIndices] = useState([])           // varios headers abiertos
	const [openSubHeaderTitles, setOpenSubHeaderTitles] = useState([])
	// varios subheaders abiertos por título
	const [windowDimensions, setWindowDimensions] = useState(
		getWindowDimensions()
	)

	// CAMBIO 2: Estado para el texto del filtro
	const [filterText, setFilterText] = useState('')
	const [companyLogoSrc, setCompanyLogoSrc] = useState(null)
	const companyLogoFrameRef = useRef(null)
	const closeStickyRef = useRef(null)
	const [stickyOffset, setStickyOffset] = useState(150)

	const getLogoFitScore = (naturalWidth, naturalHeight, frameWidth, frameHeight) => {
		if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return -1

		const containScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight)
		const renderedWidth = naturalWidth * containScale
		const renderedHeight = naturalHeight * containScale
		const widthRatio = renderedWidth / frameWidth
		const heightRatio = renderedHeight / frameHeight
		const usedRatio = Math.max(widthRatio, heightRatio)

		const verticalPenalty = heightRatio > 0.94 ? (heightRatio - 0.94) * 0.15 : 0
		const compactPenalty = widthRatio < 0.56 ? (0.56 - widthRatio) * 0.35 : 0
		return usedRatio - verticalPenalty - compactPenalty
	}

	useEffect(() => {
		const menuLogo = props.user?.sysCompanyImage?.menu_logo_url || null
		const defaultLogo = props.user?.sysCompanyImage?.logo_url || null
		const candidates = [menuLogo, defaultLogo].filter(Boolean)

		if (!candidates.length) {
			setCompanyLogoSrc(null)
			return
		}

		if (candidates.length === 1) {
			setCompanyLogoSrc(candidates[0])
			return
		}

		const frameEl = companyLogoFrameRef.current
		const frameWidth = frameEl?.clientWidth || 0
		const frameHeight = frameEl?.clientHeight || 0

		if (!frameWidth || !frameHeight) {
			setCompanyLogoSrc(menuLogo || defaultLogo)
			return
		}

		let cancelled = false

		const loadDimensions = (src) => {
			return new Promise((resolve) => {
				const image = new Image()
				image.onload = () => {
					resolve({
						src,
						naturalWidth: image.naturalWidth || 0,
						naturalHeight: image.naturalHeight || 0,
					})
				}
				image.onerror = () => resolve(null)
				image.src = src
			})
		}

		Promise.all(candidates.map(loadDimensions)).then((results) => {
			if (cancelled) return

			const ranked = (results || [])
				.filter(Boolean)
				.map((item) => ({
					...item,
					score: getLogoFitScore(
						item.naturalWidth,
						item.naturalHeight,
						frameWidth,
						frameHeight
					),
				}))
				.sort((a, b) => b.score - a.score)

			setCompanyLogoSrc(ranked[0]?.src || menuLogo || defaultLogo)
		})

		return () => {
			cancelled = true
		}
	}, [
		props.user?.sysCompanyImage?.menu_logo_url,
		props.user?.sysCompanyImage?.logo_url,
		windowDimensions.width,
		expand,
	])

	// Sincronizar lateralMenu con expand en TIEMPO REAL
	// Este efecto reacciona cada vez que lateralMenu cambia (desde ModalMenu u otro lugar)
	useEffect(() => {
		if (mobileOnly || lateralMenu === null) return;

		if (lateralMenu === true) {
			// Fijado: forzar sidebar abierto
			setExpand(1);
		} else if (lateralMenu === false) {
			// En modo tabs no autocerrar al navegar
			if (typeof onTabOpen !== 'function') setExpand(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lateralMenu]);

	useEffect(() => {
		function handleResize() {
			if (windowDimensions !== getWindowDimensions())
				setWindowDimensions(getWindowDimensions())
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	useEffect(() => {
		if (windowDimensions.width <= 425) {
			setMobileOnly(true)
			if (expand === 1 || expand == null) setExpand(1)
		} else {
			setMobileOnly(false)
		}
	}, [windowDimensions])

	useEffect(() => {
		const updateStickyOffset = () => {
			if (!closeStickyRef.current) return
			const nextOffset = closeStickyRef.current.offsetHeight || 0
			if (nextOffset > 0) setStickyOffset(nextOffset)
		}

		const id = window.requestAnimationFrame(updateStickyOffset)
		window.addEventListener('resize', updateStickyOffset)
		return () => {
			window.cancelAnimationFrame(id)
			window.removeEventListener('resize', updateStickyOffset)
		}
	}, [expand, filterText, companyLogoSrc, windowDimensions.width])

	// Mantener estado de expansión al navegar (solo asegurar visible en móvil si quieres)
	// useEffect(() => {
	// 	if (mobileOnly) setExpand(0)
	// }, [location, mobileOnly])

	const handleClickLink = (route) => {
		props.actionClick(route)
	}

	// Opens the route in a tab (if onTabOpen is provided), otherwise falls back to normal navigation
	const handleTabOpen = (e, route) => {
		if (typeof onTabOpen === 'function') {
			e.preventDefault()
			handleClickLink(route)
			onTabOpen(route)
		}
	}

	const normalizeGroup = (s) =>
		(s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

	const isImageIcon = (v) =>
		typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('data:'))

	const groupIconMap = React.useMemo(() => {
		const map = new Map()
		icons.forEach(i => map.set(normalizeGroup(i.title), i.url))
		return map
	}, [])

	const getHeaderIconValue = (header) => {
		const key = normalizeGroup(header.module_group)
		return groupIconMap.get(key) ?? header.icon ?? 'archive'
	}

	const handleHeaderClick = (event, title) => {
		event.preventDefault()
		event.stopPropagation()
		const { index } = title
		manualToggleForPath.current = currentPath

		const isOpening = !openHeaderIndices.includes(index)
		setOpenHeaderIndices(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]))

		const header = headers[index]
		if (!header) return

		if (typeof onMenuOpen === 'function') {
			const headerKey = header.module_group

			if (isOpening) {
				// Trae TODO lo que pertenezca a este grupo
				const routesForHeader = collectRoutesFor({ headerKey: headerKey })

				console.log(`Abriendo Header ${headerKey}:`, routesForHeader.length, 'items')
				onMenuOpen({
					type: 'header',
					key: normalizeGroup(headerKey),
					header,
					routes: routesForHeader
				})
			} else {
				// Al cerrar el grupo principal, limpiamos el BentoGrid (volver a Recientes)
				onMenuOpen(null)
			}
		}
	}

	const handleSubHeaderClick = (event, subTitle) => {
		event.preventDefault()
		event.stopPropagation()
		manualToggleForPath.current = currentPath

		const isOpening = !openSubHeaderTitles.includes(subTitle)

		// Actualizar estado visual del acordeón
		setOpenSubHeaderTitles(prev => (prev.includes(subTitle) ? prev.filter(t => t !== subTitle) : [...prev, subTitle]))

		// LÓGICA DE ENVÍO AL BENTOGRID
		if (typeof onMenuOpen === 'function') {
			if (isOpening) {
				// 1. Recolectamos SOLO las rutas de este subgrupo (ej. Clientes)
				const routesForSub = collectRoutesFor({ subTitle })

				// 2. Enviamos al BentoGrid
				console.log(`Abriendo SubMenu ${subTitle}:`, routesForSub.length, 'items')
				onMenuOpen({
					type: 'subheader',
					key: normalizeGroup(subTitle),
					subTitle,
					routes: routesForSub // <--- Aquí va la lista filtrada
				})
			} else {
				// Si estamos cerrando el submenú, intentamos mostrar las opciones del grupo padre
				// Para que no se quede el BentoGrid vacío
				const parentGroup = routes.find(r => normalizeGroup(r.sub_group) === normalizeGroup(subTitle))?.group

				if (parentGroup) {
					const routesForHeader = collectRoutesFor({ headerKey: parentGroup })
					onMenuOpen({
						type: 'header', // Volvemos a modo Header
						key: normalizeGroup(parentGroup),
						header: headers.find(h => h.module_group === parentGroup),
						routes: routesForHeader
					})
				} else {
					onMenuOpen(null) // Si no hay padre, limpiar
				}
			}
		}
	}

	// cuando se hace click en una ruta sin subgrupo, emitir esa ruta (útil para el panel)
	const handleRouteClickEmit = (route) => {
		if (typeof onMenuOpen === 'function') {
			onMenuOpen({
				type: 'route',
				key: route.key || route.to || route.title,
				route,
				routes: [{ ...route, icon: route.icon || null }],
			})
		}
	}

	// Memoized computations — evita recalcular en cada render
	const modulesToRender = React.useMemo(() => {
		const visibleModules = filterModulesForDisplay(user?.modules, user)
		return visibleModules.flatMap((module) => {
			if (module.url !== 'importacion') return [module]

			const processRoutes = [
				...REQUIRED_IMPORTATION_PROCESSES.map((process) => ({ ...process, sub_group: 'Obligatorias' })),
				...OPTIONAL_IMPORTATION_PROCESSES.map((process) => ({ ...process, sub_group: 'Opcionales' })),
			]

			return processRoutes.map(({ key, name, sub_group }) => ({
				...module,
				id: `${module.id || module.url}-${key}`,
				name,
				url: `importacion/${key}`,
				sub_group,
				is_tab: false,
				isImportationProcess: true,
			}))
		})
	}, [user?.modules, user])


	const headers = React.useMemo(() => {
		return modulesToRender.reduce((accu, module) => {
			const x = accu.find((item) => item.module_group == module.module_group)
			return x ? accu : accu.concat([module])
		}, [])
	}, [modulesToRender])

	const subHeaders = React.useMemo(() => {
		const arr = modulesToRender.reduce((accu, module) => {
			if (!module.sub_group) return accu
			if (!accu.find((item) => item.sub_group == module.sub_group)) {
				accu.push({
					title: module.sub_group,
					group: module.module_group,
					sub_group: module.sub_group,
					...module,
				})
			}
			return accu
		}, [])
		return _.uniqBy(arr, (item) => `${item.group}-${item.sub_group}`)
	}, [modulesToRender])


	const routes = React.useMemo(() => {
		let baseRoutes = modulesToRender
			.map((m) => {
				if (!m.isVisible) return null
				const iconValue = m.icon || m.menuIcon || m.fa_icon || null
				const children = Array.isArray(m.children) ? m.children.map(c => ({ ...c, icon: c.icon || null })) : []
				return {
					title: m.name,
					to: m.url,
					group: m.module_group,
					groupKey: normalizeGroup(m.module_group),
					sub_group: m.sub_group,
					subGroupKey: normalizeGroup(m.sub_group),
					icon: iconValue,
					children,
					...m,
				}
			})
			.filter(Boolean);
		return baseRoutes;
	}, [modulesToRender]);

	// Usar comparación normalizada para evitar diferencias de acentos/mayúsculas
	const collectRoutesFor = (opts = {}) => {
		if (!Array.isArray(routes)) return []
		if (opts.subTitle) {
			const targetSub = normalizeGroup(opts.subTitle)
			console.log('Filtrando por Subgrupo:', targetSub) // Debug
			return routes
				.filter(r => normalizeGroup(r.sub_group) === targetSub)
				.map(r => ({
					...r,
					icon: r.icon || null,
					// Aseguramos pasar children si existen
					children: (r.children || []).map(c => ({ ...c, icon: c.icon || null }))
				}))
		}
		if (opts.headerKey) {
			const targetGroup = normalizeGroup(opts.headerKey)
			console.log('Filtrando por Grupo:', targetGroup) // Debug

			return routes
				.filter(r => normalizeGroup(r.group) === targetGroup)
				.map(r => ({
					...r,
					icon: r.icon || null,
					children: (r.children || []).map(c => ({ ...c, icon: c.icon || null }))
				}))
		}
		return []
	}


	// Ruta activa para resaltar y abrir el menú correcto (robusta)
	const stripQueryHash = (s = '') => s.split('#')[0].split('?')[0]
	const normalizePath = (s) => {
		const clean = stripQueryHash(s || '/').toLowerCase().trim()
		const withSlash = ('/' + clean).replace(/\/+/g, '/')
		return withSlash !== '/' ? withSlash.replace(/\/$/, '') : '/'
	}

	// Soporta HashRouter: si hay hash, úsalo como path real
	const getRawPath = (loc) => {
		if (typeof loc?.hash === 'string' && loc.hash.startsWith('#')) {
			return loc.hash.slice(1) // sin '#'
		}
		return loc?.pathname || '/'
	}

	// Quita prefijos base como /dashboard del pathname real
	const stripBasePrefix = (pathname) => {
		const norm = normalizePath(pathname)
		const parts = norm.split('/') // ['', 'dashboard', 'modulo', 'proceso'...]
		const baseSet = new Set(['dashboard', 'home', 'inicio'])
		return (parts.length > 2 && baseSet.has(parts[1])) ? '/' + parts.slice(2).join('/') : norm
	}

	// Comparación por segmentos (exacto o prefijo con '/')
	const equalsOrParentOf = (current, target) =>
		current === target || current.startsWith(target + '/')

	const rawPath = getRawPath(location)
	const currentPath = normalizePath(rawPath)       // completo (p.ej. /dashboard/cat/empleados)
	const shouldPreserveSidebarState = isTabSwitchNavigation(location)
	// const appPath = stripBasePrefix(rawPath)         // sin base (p.ej. /cat/empleados)

	const appPath = stripBasePrefix(rawPath)
	// sin base (p.ej. /cat/empleados)
	// Forzar sidebar contraído al entrar al dashboard (caso post-login)
	useEffect(() => {
		if (shouldPreserveSidebarState) return
		// Detectar entrada al "inicio" de la app (ruta que suele abrirse después del login)
		const isEntryPoint =
			appPath === '/' ||
			currentPath === '/' ||
			rawPath === '/dashboard' ||
			rawPath === '/home' ||
			rawPath === '/inicio'

		if (isEntryPoint) {
			// cerrar cualquier header/subheader abierto y evitar auto-open inmediato
			setOpenHeaderIndices([])
			setOpenSubHeaderTitles([])
			manualToggleForPath.current = currentPath
		}
		// Ejecutar cada vez que cambia la ubicación (se entra al dashboard tras login)
	}, [location.pathname, location.hash, shouldPreserveSidebarState])

const isActiveRoute = (to) => {
		const target = normalizePath(`/${to}`)         // normaliza el to
		// Coincide exacto o como prefijo por segmentos, con y sin prefijo base
		return equalsOrParentOf(appPath, target) || equalsOrParentOf(currentPath, target)
	}

	const normalizeTabPath = (path) => {
		const normalized = `/${path || ''}`.replace(/\/+/g, '/')
		return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
	}

	const isRouteInOpenTab = (routePath) => {
		if (!openTabs || !Array.isArray(openTabs)) return false
		const normalized = normalizeTabPath(routePath)
		return openTabs.some(tab => normalizeTabPath(tab.path) === normalized)
	}

	const isRouteInActiveTab = (routePath) => {
		if (!openTabs || !activeTabId) return false
		const normalized = normalizeTabPath(routePath)
		const activeTab = openTabs.find(tab => tab.id === activeTabId)
		return activeTab && normalizeTabPath(activeTab.path) === normalized
	}

	// modificar tamaños , en caso de no (quitar span)

	// Nivel actual y helpers de clase por profundidad
	const activeRoute = routes.find((r) => isActiveRoute(r.to))
	const activeGroup = activeRoute?.group || null
	const activeSubGroup = activeRoute?.sub_group || null

	// Encoge el label del header si está abierto
	const getHeaderDepthClass = (headerIndex) =>
		openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''

	// Encoge el label del subheader si está abierto
	const getSubHeaderDepthClass = (subHeaderTitle) =>
		openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-parent' : ''

	// Rutas: normales cuando su subheader está abierto
	const getRouteDepthClassForSubHeader = (subHeaderTitle) =>
		openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-current' : ''

	// Rutas sin subgrupo: normales cuando su header está abierto
	const getRouteDepthClassForHeader = (headerIndex) =>
		openHeaderIndices.includes(headerIndex) ? 'depth-current' : ''

	// Auto-abrir el grupo/subgrupo de la ruta actual:
	// - Si el usuario hizo un toggle manual en la misma ruta, NO reabrir.
	// - Si navegas a otra ruta, reintentar auto-apertura para la nueva ruta.
	useEffect(() => {
		if (expand === 0) return // Solo prevenir auto-open si está completamente oculto
		if (shouldPreserveSidebarState) return
		if (!routes?.length || !headers?.length) return

		// evitar auto-open si el usuario hizo toggle manual en la misma ruta
		if (manualToggleForPath.current === currentPath) {
			manualToggleForPath.current = null
			return
		}

		const active = routes.find(r => isActiveRoute(r.url))
		if (!active) return

		const hIndex = headers.findIndex(h => h.module_group === active.group)
		if (hIndex >= 0) {
			setOpenHeaderIndices(prev => (prev.includes(hIndex) ? prev : [...prev, hIndex]))
		}
		if (active.sub_group) {
			setOpenSubHeaderTitles(prev => (prev.includes(active.sub_group) ? prev : [...prev, active.sub_group]))
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPath, routes, headers, expand, shouldPreserveSidebarState])

	const activeItemRef = useRef(null)
	useEffect(() => {
		if (shouldPreserveSidebarState) return
		// Espera a que termine la animación del Transition (~220ms)
		const id = setTimeout(() => {
			if (activeItemRef?.current) {
				activeItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
			}
		}, 240)
		return () => clearTimeout(id)
		// Además de ruta/expand/fijado, reacciona al abrir/cerrar headers y subheaders
	}, [currentPath, expand, shouldPreserveSidebarState])

	return (
		<div
			className='sidebar'
			style={{
				width: (expand == null || expand == 1) ? '225px' : '0px',
				overflowY: 'auto',
				overflowX: 'hidden',
			}}
		>
			{(expand == null || expand == 1) && (
				<div className='Sidebar__CloseStickyy' ref={closeStickyRef} style={{ padding: 0, margin: 0 }}>
					<div className='Sidebar__Header'>
						{/* Fila 1: Solo botón hamburguesa */}
						<div className='Sidebar__MenuButtonRow' style={{justifyContent:'flex-start',paddingLeft:'0',paddingRight:'0'}}>
							<button
								className='Sidebar__MenuButton Sidebar__MenuButton--shell'
								onClick={() => {
									if (lateralMenu && !mobileOnly) {
										return
									}

									setExpand((previousExpandedState) => {
										setPreviousExpand(previousExpandedState)
										const newExpandState = !mobileOnly
											? previousExpandedState == 1
												? (previousExpand == null || previousExpand == 2) ? 0 : 2
												: 1
											: !previousExpandedState ? 1 : 0

										return newExpandState
									})
								}}
								aria-label={t('Abrir/cerrar menú lateral')}
								style={{background:'transparent',border:'1.1px solid #6c6c6c',borderRadius:7,padding:'0 13px 0 3px',height:26,minWidth:62,display:'flex',alignItems:'center',gap:3,boxShadow:'none',justifyContent:'flex-start',textAlign:'left'}}
							>
								<img
									src='https://img.icons8.com/?size=100&id=100033&format=png&color=000000'
									alt='Menu'
									style={{width:14.5,height:14.5,marginRight:-6,marginLeft:0,display:'inline-block'}}
								/>
								<span style={{fontSize:13,color:'#6c6c6c',fontWeight:500,whiteSpace:'nowrap',marginLeft:0}}>ocultar</span>
							</button>
						</div>

						{/* Fila 2: Logo expandido */}
						<div className='Sidebar__LogoRow'>
							<div className='Sidebar__LogoSlot'>
								<div className='Sidebar__LogoFrame' ref={companyLogoFrameRef}>
									{companyLogoSrc ? (
										<img
											src={companyLogoSrc}
											alt='Company Logo'
											className='Sidebar__CompanyLogo SideBar__Outline'
											onError={() => {
												setCompanyLogoSrc(props.user?.sysCompanyImage?.logo_url || null)
											}}
											onClick={e => e.stopPropagation()}
											onMouseDown={e => e.stopPropagation()}
										/>
									) : (
										<div className='Sidebar__LogoPlaceholder' />
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Barra de búsqueda dentro del sticky para reducir espacio */}
					<div className='SidebarSearchContainer' style={{ padding: '2px 12px 6px 12px' }}>
						<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
							<div style={{ width: '95%' }}>
								<SidebarSearch
									fluid
									className='SearchInput SidebarSearchInput'
									inputStyle={{ width: '100%', height: 45, fontSize: 15, paddingTop: 7, paddingBottom: 7 }}
									placeholder={t("Buscar Proceso")}
									value={filterText}
									open={false}
									results={[]}
									onSearchChange={(e, { value }) => setFilterText(value)}
								/>
							</div>
							<div style={{ width: '15%', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
							</div>
						</div>
					</div>
				</div>
			)
			}
			<Transition
				visible={expand == null || expand == 1}
				animation='fade down'
				duration={220}
				unmountOnHide={false}
			>
				<div style={{ marginTop: stickyOffset }}>

					{/* Filtered data based on filterText */}
					{(() => {
						const normalizeFilter = normalizeSidebarSearch
						const filter = normalizeFilter(filterText)
						const singularizedFilter = filter.split(/\s+/)
							.map((word) => word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word)
							.join(' ')
						const matchesFilter = (values) => (
							matchesSidebarSearch(values, filter)
							|| values.some((value) => normalizeFilter(value).includes(singularizedFilter))
						)
						const hiddenRouteNames = ['Lista de inspecciones de unidades', 'Pagos']
						const hiddenRouteNamesNormalized = new Set(hiddenRouteNames.map(normalizeFilter))
						const isHiddenRoute = (route) => {
							if (!route) return false
							const candidates = [
								route.name,
								route.title,
								route?.name ? t(route.name) : '',
								route?.title ? t(route.title) : '',
							]
							return candidates.some((candidate) => hiddenRouteNamesNormalized.has(normalizeFilter(candidate)))
						}

						// Función para resaltar texto, adaptando color según fondo
						const highlightText = (text, isActive, onlyBold = false) => {
							if (!filter || !text) return text
							const parts = splitSidebarSearchMatches(text, filter)

							return parts.map(({ text: part, match }, i) => {
								if (match) {
									if (onlyBold) {
										return <span key={i} style={{ fontWeight: 'bold' }}>{part}</span>
									}
									return (
										<span
											key={i}
											className='sidebar-search-highlight'
											style={{
												backgroundColor: isActive ? '#fa9c22d5' : '#fffde7',
												fontWeight: 'bold',
												borderRadius: '2px',
												color: isActive ? '#111' : undefined,
												padding: '0 2px',
											}}
										>
											{part}
										</span>
									)
								}
								return part
							})
						}

						// 1. Filtrar primero las rutas individuales
						const filteredRoutes = filter ? routes.filter(r => {
							return !isHiddenRoute(r) && matchesFilter([
								r.title, r.name, r.name_en, r.name_pt, r.name_ar, r.group, r.sub_group,
								r.name ? t(r.name) : '',
								r.group ? t(r.group) : '',
								r.sub_group ? t(r.sub_group) : '',
							])
						}) : routes.filter(route => !isHiddenRoute(route))

						// 2. Filtrar subheaders: incluir si ellos coinciden O si alguna de sus rutas coincide
						const filteredSubHeaders = filter ? subHeaders.filter(sh => {
							const matchesSelf = matchesFilter([
								sh.title, sh.name_en, sh.name_pt, sh.name_ar,
								sh.title ? t(sh.title) : '',
							])
							const hasMatchingRoute = filteredRoutes.some(r => r.sub_group === sh.title && r.group === sh.group)

							return matchesSelf || hasMatchingRoute
						}) : subHeaders

						// 3. Filtrar headers: incluir si ellos coinciden O si algún subheader o ruta directamente bajo ellos coincide
						const filteredHeaders = filter ? headers.filter(h => {
							const matchesSelf = matchesFilter([
								h.module_group, h.module_group_en, h.module_group_pt, h.module_group_ar,
								h.module_group ? t(h.module_group) : '',
							])
							const hasMatchingSubHeader = filteredSubHeaders.some(sh => sh.group === h.module_group)
							const hasMatchingRoute = filteredRoutes.some(r => r.group === h.module_group && !r.sub_group)

							return matchesSelf || hasMatchingSubHeader || hasMatchingRoute
						}) : headers

						return (
							<>
								{/* NOTA: Cambiamos 'headers' por 'filteredHeaders' para que el filtro funcione */}
								{filteredHeaders.map((header, index) => {
									// Si hay texto en el filtro, forzamos que se vea abierto
									const isHeaderOpen = filterText ? true : openHeaderIndices.includes(index)

									return (
										<Accordion
											key={`accordion-${header.module_group}-${index}`}
											fluid
											style={{ paddingLeft: '4px', paddingBottom: '5px' }}
										>

											<Accordion.Title
												key={`title-${header.module_group}-${index}`}
												data-tour-group={header.module_group}
												active={isHeaderOpen}
												index={index}
												onClick={filterText ? undefined : handleHeaderClick} // Desactiva click si filtras
												className={getHeaderDepthClass(index, header.module_group)}
												style={{ cursor: filterText ? 'default' : 'pointer' }}
											>

												<Menu.Item
													className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''} border-right: 2px solid black${activeGroup === header.module_group ? ' is-active' : ''}`}
													style={{ width: '100%', display: 'inline-block' }}
												>
													<Icon
														name='dropdown'
														style={{
															marginRight: 6,
															transform: isHeaderOpen ? 'rotate(90deg)' : 'rotate(0deg)',
															opacity: isHeaderOpen ? 1 : 0,
															transition: 'opacity 600ms cubic-bezier(.2,.8,.2,1), transform 600ms cubic-bezier(.2,.8,.2,1)',
															pointerEvents: 'none',
														}}
													/>
													{(() => {
														const isImportationGroup = normalizeGroup(header.module_group) === 'importacion'
														const iconValue = isImportationGroup ? 'upload' : getHeaderIconValue(header)
														return isImageIcon(iconValue) ? (
															<img
																src={iconValue}
																alt={header.module_group}
																style={{ width: 24, height: 24, verticalAlign: 'middle', marginRight: 8 }}
																className='SideBar__Outline'
															/>
														) : (
															<Icon
																name={iconValue}
																size='large'
																className={`SideBar__Outline ${isImportationGroup ? 'Importation__Outline' : ''}`}
															/>
														)
													})()}
													{(expand == null || expand == 1) && (
														<h5 className={`menu-row-title ${getHeaderDepthClass(index)}`}>
															{highlightText(t(header.module_group), false, true)}
														</h5 >
													)}

												</Menu.Item >
											</Accordion.Title >
											<Transition
												visible={isHeaderOpen}
												animation='fade down'
												duration={220}
												unmountOnHide={false}
											>

												<Accordion.Content
													key={`content-${header.module_group}-${index}`}
													style={{ marginTop: '0px', paddingTop: '0px' }}
													active={isHeaderOpen}
												>
													<Grid divided='vertically' style={{ width: '100%', }}>
														<Grid.Row style={{ paddingBottom: 0 }} />
														<Grid.Row style={{ paddingTop: '0', paddingBottom: '0', paddingLeft: '15px' }}>
															<Grid.Column style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}>
																{/* Usamos filteredSubHeaders en vez de subHeaders */}
																{filteredSubHeaders.map((subHeader, indexH) => {
																	if (subHeader.group == header.module_group) {
																		const isSubOpen = filterText ? true : openSubHeaderTitles.includes(subHeader.title)
																		const subHeaderRoutes = filteredRoutes.filter(
																			(route) => route.group === header.module_group && route.sub_group === subHeader.title
																		)
																		return (
																			<Accordion
																				style={{ marginTop: '0px', paddingTop: '0px' }}
																				key={`accordion-${subHeader.title}-${indexH}`}
																				fluid
																				className='menu_row'
																			>
																				<Accordion.Title
																					key={`title-${subHeader.title}-${indexH}`}
																					style={{ marginBottom: '0px', paddingBottom: '0px' }}
																					active={isSubOpen}
																					onClick={filterText ? undefined : (e) => handleSubHeaderClick(e, subHeader.title)}
																					className={`${getSubHeaderDepthClass(subHeader.title)} ${filteredRoutes.some(r => r.sub_group === subHeader.title && isActiveRoute(r.to)) ? 'has-active' : ''}`}
																					data-tour={`subroute-${subHeader.title}`}
																				>
																					<Icon name='dropdown' />
																					<span className={`menu-row-title ${getSubHeaderDepthClass(subHeader.title)}`}>
																						{highlightText((() => {
																							// (erandi): De preferencia que no se comparen strings directamente,
																							// pero bueno ya que.
																							let title = t(subHeader.title);
																							if (title === 'Catálogos de Viajes') return 'Catálogos';
																							if (title === 'Pagos de T. Mercancía') return 'Pagos';
																							if (title === 'CFDI de T. Mercancía') return 'CFDI';
																							if (title === 'Gestión de rutas' || title === 'Gestion de rutas' || title === 'Gestión de Rutas') {
																								return 'Gestión de rutas/Geo Transport';
																							}
																							return title;
																						})(), false, true)}
																					</span>
																				</Accordion.Title>
																				<Transition
																					visible={isSubOpen}
																					animation='fade down'
																					duration={220}
																					unmountOnHide={false}
																				>
																					<Accordion.Content
																						key={`content-${subHeader.title}-${indexH}`}
																						style={{ marginTop: '0px', paddingTop: '0px', backgroundColor: 'rgba(248, 3, 3, 0.02)' }}
																						active={isSubOpen}
																						data-tour={`subcontentroute-${subHeader.title}`}
																					>
																						<Menu vertical fluid text >
																							{/* Usamos filteredRoutes en vez de routes */}
																							{subHeaderRoutes.map((route, indexR) => (
																								<Link
																									target={user?.modules_target_blank_mode ? '_blank' : ''}
																									key={`link-${indexR}-${route.title}`}
																									to={`/${route.to}`}
																									className={`sub_menu_row ${isRouteInActiveTab(route.to) ? 'is-active' : ''} ${isRouteInOpenTab(route.to) && !isRouteInActiveTab(route.to) ? 'has-tab-open' : ''}`}
																									style={{ width: '100%', display: 'block' }}
																									data-tour={`subroute-${route.to}`}
																									onClick={(e) => { handleTabOpen(e, route); handleRouteClickEmit(route) }}
																								>
																									<Menu.Item
																										className={styles.growOnHover}
																										style={{ margin: '0' }}
																									>
																										<span
																											ref={isActiveRoute(route.to) ? activeItemRef : null}
																											className={`menu-item-label ${getRouteDepthClassForSubHeader(subHeader.title)}`}
																										>
																											{highlightText(t(route.name), isActiveRoute(route.to))}
																										</span >
																									</Menu.Item >
																								</Link >
																							))}
																						</Menu >
																					</Accordion.Content >
																				</Transition >
																			</Accordion >
																		)
																	}
																})}

																{
																	(() => {
																		const routesWithoutSubgroup = filteredRoutes.filter(
																			route => route.group === header.module_group && !route.sub_group
																		)
																		const hasContent = routesWithoutSubgroup.length > 0

																		return (
																			<Menu
																				vertical
																				fluid
																				text
																				style={{
																					width: '100%',
																					minHeight: hasContent ? 'auto' : '10px',
																					height: hasContent ? 'auto' : '10px',
																					margin: hasContent ? undefined : 0,
																					padding: hasContent ? undefined : 0
																				}}
																			>
																				{routesWithoutSubgroup.map((route, indexR) => (
																					<Link
																						target={user?.modules_target_blank_mode ? '_blank' : ''}
																						key={`link-${indexR}-${route.title}`}
																						to={`/${route.to}`}
className={`sub_menu_row ${isRouteInActiveTab(route.to) ? 'is-active' : ''} ${isRouteInOpenTab(route.to) && !isRouteInActiveTab(route.to) ? 'has-tab-open' : ''}`}
																						data-tour={`route-${route.to}`}
																						onClick={(e) => handleTabOpen(e, route)}
																					>
																						<Menu.Item
																							style={{ margin: '0' }}
																						>
																							<span
																								ref={isActiveRoute(route.to) ? activeItemRef : null}
																								className={`menu-item-label ${getRouteDepthClassForHeader(index)}`}
																							>
																								{highlightText(t(route.name), isActiveRoute(route.to))}
																							</span >
																						</Menu.Item >
																					</Link >
																				))
																				}
																			</Menu >
																		)
																	})()}
															</Grid.Column >
														</Grid.Row >
													</Grid >
												</Accordion.Content >
											</Transition >
										</Accordion >
									)
								})}
							</>
						)
					})()}
				</div >
			</Transition >
		</div >
	)
}

const mapStateToProps = (state) => ({
	user: state.app.user,
	lang: state.app.lang, // Añadido lang
})

const mapDispatchToProps = (dispatch) => ({
	actionClick: (data) => dispatch(actionClick(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SidebarMenu)
